<template>
  <!-- Outer wrapper -->
  <div class="relative h-full flex flex-col">

    <!-- Tab Selector -->
    <div v-reveal="{ y: 16 }" class="flex justify-center mb-5">
      <div class="inline-flex items-center gap-1 rounded-md border border-gray-200/70 bg-white p-1 shadow-sm" role="tablist">
        <button
          @click="setActiveType('newspaper')"
          role="tab"
          :aria-selected="activeType === 'newspaper'"
          :class="activeType === 'newspaper' ? 'bg-brand-red text-white shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'"
          class="inline-flex items-center gap-2 px-5 py-2 rounded text-sm font-semibold transition-colors"
        >
          <i class="fa-regular fa-newspaper"></i>
          Newspapers
        </button>

        <button
          @click="setActiveType('magazine')"
          role="tab"
          :aria-selected="activeType === 'magazine'"
          :class="activeType === 'magazine' ? 'bg-brand-red text-white shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'"
          class="inline-flex items-center gap-2 px-5 py-2 rounded text-sm font-semibold transition-colors"
        >
          <i class="fa-solid fa-book-open"></i>
          Magazines
        </button>
      </div>
    </div>

    <!-- Main content -->
    <main class="relative z-10 w-full flex-1 flex flex-col gap-5 px-2 sm:px-6 md:px-0">

  <!-- Error -->
  <div v-if="error" class="card p-6 text-center text-brand-red-dark font-medium">
    <i class="fa-solid fa-triangle-exclamation mr-2"></i>{{ error }}
  </div>

  <!-- No Issues -->
  <section
    v-else-if="paginatedIssues.length === 0"
    class="card p-10 flex-1 flex items-center justify-center"
  >
    <p class="text-gray-500 text-center">
      No {{ activeType }} available.
    </p>
  </section>

  <!-- Issues Exist -->
  <template v-else>

    <!-- Payment Notice -->
    <div class="flex items-start gap-3 rounded-md border border-brand-cream-dark/70 bg-brand-cream/40 px-4 py-3 text-sm text-gray-800">
      <i class="fa-solid fa-circle-info mt-0.5 text-brand-red-dark"></i>
      <p v-if="activeType === 'newspaper'">
        <strong>Note:</strong> To download the latest month's newspaper, a payment of <strong>₹20</strong> is required.
      </p>

      <p v-else>
        <strong>Note:</strong> To download any magazine, a payment of <strong>₹100</strong> is required.
      </p>
    </div>

    <!-- Issues List -->
    <section class="card px-6 py-8 md:px-10 md:py-10 flex-1 flex flex-col">
      <div v-for="issue in paginatedIssues" :key="issue.id" class="issue-card text-center h-full flex flex-col">
        <div class="rounded-md bg-gradient-to-b from-gray-50 to-gray-100/60 py-6 md:py-8 px-4 mb-6">
          <img
            :src="getFullUrl(issue.thumbnail_path)"
            :alt="issue.title"
            class="mx-auto w-auto max-w-full h-64 md:h-[380px] object-contain rounded-md shadow-xl ring-1 ring-black/5 bg-white"
          />
        </div>
        <h2 class="font-serif text-2xl md:text-[28px] font-bold leading-snug mb-2 text-gray-900">
          {{ issue.title }}
        </h2>
        <p class="text-gray-600 text-[15px] leading-relaxed mb-auto max-w-xl mx-auto">
          {{ issue.description }}
        </p>
        <div class="mt-6">
          <button
            @click="handleDownload(issue)"
            class="px-8 py-3 hover:-translate-y-0.5"
            :class="((activeType === 'newspaper' && issue.is_paid) || activeType === 'magazine') && !userAccess[issue.id]
              ? 'btn-primary'
              : 'btn-cream'"
          >
            <template v-if="((activeType === 'newspaper' && issue.is_paid) || activeType === 'magazine') && !userAccess[issue.id]">
              <i class="fa-solid fa-lock text-xs"></i>
              <span>Buy to Download</span>
            </template>
            <template v-else>
              <i class="fa-solid fa-download text-xs"></i>
              <span>Download PDF</span>
            </template>
          </button>
        </div>
      </div>
    </section>

  </template>

  <!-- Pagination -->
  <nav v-if="totalPages > 1" class="flex flex-wrap justify-center items-center gap-1.5" aria-label="Pagination">
    <button
      @click="prevPage"
      :disabled="currentPage === 1"
      class="pagination-btn inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:border-brand-red hover:text-brand-red transition-colors disabled:opacity-40 disabled:pointer-events-none"
    >
      <i class="fa-solid fa-chevron-left text-[10px]"></i>
      <span class="hidden sm:inline">Previous</span>
    </button>

    <button
      v-for="(page, index) in paginationPages"
      :key="index"
      @click="page !== '...' && goToPage(page)"
      :disabled="page === '...'"
      :aria-current="currentPage === page ? 'page' : undefined"
      :class="[
        'pagination-btn h-9 min-w-[2.25rem] px-2 rounded-md text-sm font-medium tabular-nums transition-colors',
        page === '...' ? 'bg-transparent text-gray-400 cursor-default' :
        currentPage === page
          ? 'bg-brand-red text-white font-semibold shadow-sm'
          : 'bg-white border border-gray-200 text-gray-700 hover:border-brand-red hover:text-brand-red',
      ]"
    >
      {{ page }}
    </button>

    <button
      @click="nextPage"
      :disabled="currentPage === totalPages"
      class="pagination-btn inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:border-brand-red hover:text-brand-red transition-colors disabled:opacity-40 disabled:pointer-events-none"
    >
      <span class="hidden sm:inline">Next</span>
      <i class="fa-solid fa-chevron-right text-[10px]"></i>
    </button>
  </nav>

</main>
  </div>
</template>

<script setup>
import Swal from 'sweetalert2'
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { openPdf, errorMessage } from '@/api'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

// State
const activeType = ref('newspaper') // 'newspaper' or 'magazine'
const issues = ref([])
const error = ref('')
const currentPage = ref(1)
const itemsPerPage = 1 // Show 1 PDF at a time
const userAccess = ref({})

// API Base URLs
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL
const FILE_BASE_URL = process.env.VUE_APP_FILE_BASE_URL

// Computed for pagination
const totalPages = computed(() =>
  Math.ceil(issues.value.length / itemsPerPage)
)

const paginationPages = computed(() => {
  const current = currentPage.value;
  const total = totalPages.value;
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 3) return [1, 2, 3, 4, '...', total];
  if (current >= total - 2) return [1, '...', total - 3, total - 2, total - 1, total];
  return [1, '...', current - 1, current, current + 1, '...', total];
});

const paginatedIssues = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  return issues.value.slice(start, start + itemsPerPage)
})

// Animations
const animateItems = () => {
  nextTick(() => {
    gsap.from('.issue-card', {
      scrollTrigger: {
        trigger: '.issue-card',
        start: 'top 85%',
        toggleActions: 'play none none none',
      },
      y: 50,
      opacity: 0,
      duration: 0.8,
      stagger: 0.2,
      ease: 'power2.out',
    })

    gsap.from('.pagination-btn', {
      opacity: 0,
      scale: 0.95,
      duration: 0.6,
      stagger: 0.1,
      ease: 'back.out(1.7)',
    })
  })
}

// Fetch data
const checkAccessForIssues = async () => {
  const token = localStorage.getItem('token')
  if (!token) return

  userAccess.value = {}
  await Promise.all(issues.value.map(async (issue) => {
    if ((activeType.value === 'newspaper' && issue.is_paid) || activeType.value === 'magazine') {
      try {
        const res = await axios.get(`${API_BASE_URL}/${activeType.value}s/${issue.id}/access`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.data.has_access) {
          userAccess.value[issue.id] = true
        }
      } catch (err) {
        console.error('Failed to check access for issue', issue.id)
      }
    }
  }))
}

const fetchIssues = async () => {
  try {
    error.value = ''
    const response = await axios.get(`${API_BASE_URL}/${activeType.value}s`)
    issues.value = response.data
    currentPage.value = 1
    animateItems()
    checkAccessForIssues()
  } catch (err) {
    console.error(`❌ Error fetching ${activeType.value}s:`, err)
    error.value = `Failed to load ${activeType.value}s. Please try again later.`
  }
}

// Helpers
const getFullUrl = (path) => {
  if (!path) return '#'
  if (path.startsWith('http')) return path
  const base = FILE_BASE_URL?.replace(/\/$/, '') ?? ''
  return `${base}${path.startsWith('/') ? path : `/${path}`}`
}

// Pagination
const goToPage = (page) => { currentPage.value = page }
const prevPage = () => { if (currentPage.value > 1) currentPage.value-- }
const nextPage = () => { if (currentPage.value < totalPages.value) currentPage.value++ }

// Switch tab
const setActiveType = (type) => {
  if (activeType.value === type) return
  activeType.value = type
  fetchIssues()
}

// Handle Download — checks login + paid access before downloading
const router = useRouter()
const handleDownload = async (issue) => {
  const token = localStorage.getItem('token')

  // For newspapers only: check is_paid flag
  if (activeType.value === 'newspaper') {
    if (issue.is_paid) {
      // Must be logged in first
      if (!token) {
        Swal.fire({
          icon: 'warning',
          title: 'Login Required',
          text: 'Please log in to purchase and download this newspaper.',
          confirmButtonColor: '#BF3131',
          showCancelButton: true,
          confirmButtonText: 'Go to Login'
        }).then(r => { if (r.isConfirmed) router.push('/login') })
        return
      }

      // Check if user has already purchased this newspaper
      try {
        const res = await axios.get(`${API_BASE_URL}/newspapers/${issue.id}/access`, {
          headers: { Authorization: `Bearer ${token}` }
        })

        if (res.data.has_access) {
          // ✅ Paid & access confirmed — trigger download
          downloadIssue(issue)
        } else {
          // ❌ Not purchased yet — show price & redirect to subscribe
          const result = await Swal.fire({
            icon: 'info',
            title: 'Paid Issue',
            html: `To download this newspaper, a payment of <b>₹20</b> is required.<br><br>Subscribe to get access.`,
            confirmButtonColor: '#BF3131',
            confirmButtonText: 'Subscribe Now',
            showCancelButton: true,
            cancelButtonText: 'Cancel'
          })
          if (result.isConfirmed) {
            router.push({ path: '/subscribe', query: { type: 'newspaper', id: issue.id } })
          }
        }
      } catch (err) {
        console.error('Access check failed:', err)
        Swal.fire({ icon: 'error', title: 'Error', text: 'Could not verify access. Try again.', confirmButtonColor: '#BF3131' })
      }
      return
    } else {
      // Free newspaper — download directly without login
      downloadIssue(issue)
      return
    }
  }

  // For magazines: Check login and purchase access
  if (activeType.value === 'magazine') {
    if (!token) {
      Swal.fire({
        icon: 'warning',
        title: 'Login Required',
        text: 'Please log in to purchase and download this magazine.',
        confirmButtonColor: '#BF3131',
        showCancelButton: true,
        confirmButtonText: 'Go to Login'
      }).then(r => { if (r.isConfirmed) router.push('/login') })
      return
    }

    try {
      const res = await axios.get(`${API_BASE_URL}/magazines/${issue.id}/access`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (res.data.has_access) {
        // ✅ Paid & access confirmed — trigger download
        downloadIssue(issue)
      } else {
        // ❌ Not purchased yet — show price & redirect to subscribe
        const result = await Swal.fire({
          icon: 'info',
          title: 'Paid Issue',
          html: `To download this magazine, a payment of <b>₹100</b> is required.<br><br>Subscribe to get access.`,
          confirmButtonColor: '#BF3131',
          confirmButtonText: 'Subscribe Now',
          showCancelButton: true,
          cancelButtonText: 'Cancel'
        })
        if (result.isConfirmed) {
          router.push({ path: '/subscribe', query: { type: 'magazine', id: issue.id } })
        }
      }
    } catch (err) {
      console.error('Access check failed:', err)
      Swal.fire({ icon: 'error', title: 'Error', text: 'Could not verify access. Try again.', confirmButtonColor: '#BF3131' })
    }
    return
  }

  // Free issue fallback (shouldn't be reached for magazine, but just in case)
  downloadIssue(issue)
}

// Ask the server for a short-lived download link (it re-checks access), then open it
const downloadIssue = async (issue) => {
  try {
    await openPdf(activeType.value, issue.id)
  } catch (err) {
    console.error('Download failed:', err)
    Swal.fire({ icon: 'error', title: 'Download failed', text: errorMessage(err, 'Could not download this issue. Please try again.'), confirmButtonColor: '#BF3131' })
  }
}

// Initial load
onMounted(fetchIssues)
</script>
